import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ClientDiscussion } from './kng-model.assistant';

/**
 * Interface pour une discussion épinglée (côté client)
 * Utilise ClientDiscussion qui a déjà les messages filtrés
 */
export interface PinnedDiscussion extends ClientDiscussion {
  // ClientDiscussion déjà filtré (sans system/tool messages)
}

@Injectable({
  providedIn: 'root'
})
export class KngPinnedService {
  //
  // BehaviorSubject pour la liste des discussions épinglées
  private pinnedSubject = new BehaviorSubject<PinnedDiscussion[]>([]);

  //
  // Observable public pour la souscription
  pinned$ = this.pinnedSubject.asObservable();

  constructor(private $http: HttpClient) {}

  /**
   * Récupère la liste des discussions épinglées
   * @param agent - Nom de l'agent pour filtrer (optionnel)
   */
  list(agent?: string): Observable<PinnedDiscussion[]> {
    const params = agent ? `?agent=${agent}` : '';

    return this.$http.get<PinnedDiscussion[]>(`/api/agent/pinned${params}`, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
      }
    }).pipe(
      tap((pins: PinnedDiscussion[]) => {
        // Mettre à jour le BehaviorSubject
        this.pinnedSubject.next(pins);
      }),
      catchError((error) => {
        console.error('❌ Error loading pinned discussions:', error);
        this.pinnedSubject.next([]);
        return of([]);
      })
    );
  }

  /**
   * Épingle la discussion active
   * @param agent - Nom de l'agent
   */
  pin(agent: string): Observable<any> {
    return this.$http.post('/api/agent/pinned',
      { agent },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'ngsw-bypass': 'true',
          'Cache-Control': 'no-cache',
        }
      }
    ).pipe(
      tap((response: any) => {
        this.notify('Discussion épinglée avec succès', 'success', 'pin-fill');
        // Recharger la liste
        this.list(agent).subscribe();
      }),
      catchError((error) => {
        const message = error.error?.message || 'Erreur lors de l\'épinglage';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  /**
   * Supprime une discussion épinglée
   * @param id - ID de la discussion à supprimer
   */
  unpin(id: string, agent?: string): Observable<any> {
    return this.$http.delete(`/api/agent/pinned/${id}`, {
      withCredentials: true,
      headers: {
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap(() => {
        this.notify('Épingle supprimée avec succès', 'success', 'pin-slash');
        // Recharger la liste
        if (agent) {
          this.list(agent).subscribe();
        }
      }),
      catchError((error) => {
        const message = error.error?.message || 'Erreur lors de la suppression';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  /**
   * Charge une discussion épinglée (même ID)
   * @param id - ID de la discussion à charger
   */
  load(id: string): Observable<any> {
    return this.$http.post(`/api/agent/pinned/${id}/load`, {}, {
      withCredentials: true,
      headers: {
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap(() => {
        this.notify('Discussion chargée avec succès', 'success', 'box-arrow-in-down');
      }),
      catchError((error) => {
        const message = error.error?.message || 'Erreur lors du chargement';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  /**
   * Fork une discussion épinglée (nouveau ID)
   * @param id - ID de la discussion à forker
   */
  fork(id: string): Observable<any> {
    return this.$http.post(`/api/agent/pinned/${id}/fork`, {}, {
      withCredentials: true,
      headers: {
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap(() => {
        this.notify('Discussion forkée avec succès', 'success', 'bezier2');
      }),
      catchError((error) => {
        const message = error.error?.message || 'Erreur lors du fork';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  /**
   * Affiche une notification toast (style Shoelace)
   */
  private notify(message: string, variant = 'primary', icon = 'info-circle', duration = 3000) {
    const escapeHtml = (html: string) => {
      const div = document.createElement('div');
      div.textContent = html;
      return div.innerHTML;
    };

    const alert = Object.assign(document.createElement('sl-alert'), {
      variant,
      closable: true,
      duration: duration,
      innerHTML: `
        <sl-icon name="${icon}" slot="icon"></sl-icon>
        ${escapeHtml(message)}
      `
    });

    document.body.append(alert);
    return (alert as any).toast();
  }
}

